import React, { useEffect, useMemo, useRef, useState } from "react";
import feather from "feather-icons";
import Chart from "chart.js/auto";
import { ethers } from "ethers";

// ✅ Variables d’environnement (CRA-compatible)
const ADMIN_ADDRESS = (process.env.REACT_APP_ADMIN_WALLET || "").toLowerCase();
const TOKEN_ADDRESS =
  process.env.REACT_APP_TOKEN_ADDRESS ||
  "0xeD4370176558Be01cd01428b8e6842d5a0b67Ea8";
const TOKEN_SYMBOL = process.env.REACT_APP_TOKEN_SYMBOL || "NEXORA";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

const TABS = [
  { key: "overview", label: "Overview", icon: "layout" },
  { key: "stats", label: "Stats Globales", icon: "bar-chart-2" },
  { key: "txs", label: "Transactions", icon: "activity" },
  { key: "system", label: "System & Actions", icon: "settings" },
];

export default function Admin() {
  const [active, setActive] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(localStorage.getItem("wallet-address") || "");
  const [token, setToken] = useState(localStorage.getItem("NEXORA-token") || "");
  const [checkingAuth, setCheckingAuth] = useState(true);

  // 🧩 Références graphiques
  const txBarRef = useRef(null);
  const userLineRef = useRef(null);
  const presaleBarRef = useRef(null);
  const holdersDonutRef = useRef(null);
  const txBar = useRef(null);
  const userLine = useRef(null);
  const presaleBar = useRef(null);
  const holdersDonut = useRef(null);

  // 🦊 Authentification MetaMask
  useEffect(() => {
    (async () => {
      try {
        if (!window.ethereum) {
          alert("🦊 Please install MetaMask to access the admin panel.");
          setCheckingAuth(false);
          return;
        }

        const provider = new ethers.BrowserProvider(window.ethereum);
        const accounts = await provider.send("eth_requestAccounts", []);
        if (!accounts.length) {
          alert("⚠️ Please connect your wallet.");
          setCheckingAuth(false);
          return;
        }

        const address = accounts[0];
        localStorage.setItem("wallet-address", address);
        setWallet(address);

        // 🔒 Vérifie l’adresse admin
        if (!ADMIN_ADDRESS) {
          alert("Configuration manquante : REACT_APP_ADMIN_WALLET");
          setCheckingAuth(false);
          return;
        }

        if (address.toLowerCase() !== ADMIN_ADDRESS) {
          console.warn("⛔ Non-admin wallet:", address);
          setCheckingAuth(false);
          return;
        }

        // ✅ Signature simple
        let t = localStorage.getItem("NEXORA-token");
        if (!t) {
          const signer = await provider.getSigner();
          const message = `NEXORA_ADMIN_LOGIN_${new Date().toISOString()}`;
          const signature = await signer.signMessage(message);
          t = btoa(signature);
          localStorage.setItem("NEXORA-token", t);
        }

        setToken(t);
        setCheckingAuth(false);
      } catch (err) {
        console.error("Auth error:", err);
        setCheckingAuth(false);
      }
    })();
  }, []);

  // ✅ Vérification Admin
  const isAdmin = useMemo(
    () => wallet && wallet.toLowerCase() === ADMIN_ADDRESS,
    [wallet]
  );

  useEffect(() => {
    feather.replace();
  }, [active, sidebarOpen]);

  // 📊 Chargement des stats une fois connecté
  useEffect(() => {
    if (!checkingAuth && isAdmin && token) {
      fetchStats();
      return () => destroyAllCharts();
    }
  }, [checkingAuth, isAdmin, token]);

  async function fetchStats() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch stats");
      const data = await res.json();
      const normalized = normalizeStats(data);
      setStats(normalized);
      setTimeout(() => drawCharts(normalized), 0);
    } catch (e) {
      console.error(e);
    }
  }

  function normalizeStats(d) {
    return {
      totalUsers: d?.totalUsers || 0,
      newUsers24h: d?.newUsers24h || 0,
      tokenHolders: d?.tokenHolders || 0,
      holders24h: d?.holders24h || 0,
      tx24h: d?.tx24h || 0,
      tx7d: d?.tx7d || 0,
      tx30d: d?.tx30d || 0,
      userGrowth: d?.userGrowth || { labels: [], values: [] },
      presaleSold: d?.presaleSold || 0,
      presaleRemaining: d?.presaleRemaining || 0,
      topHoldersPercentage: d?.topHoldersPercentage || 0,
      paused: !!d?.paused,
      treasuryBalance: d?.treasuryBalance || 0,
      recentTransactions: d?.recentTransactions || [],
    };
  }

  // 🧹 Gestion des graphiques
  function destroyChart(ref) {
    if (ref.current) {
      ref.current.destroy();
      ref.current = null;
    }
  }
  function destroyAllCharts() {
    destroyChart(txBar);
    destroyChart(userLine);
    destroyChart(presaleBar);
    destroyChart(holdersDonut);
  }

  // 🎨 Création des graphiques
  function drawCharts(d) {
    destroyAllCharts();

    if (txBarRef.current) {
      const ctx = txBarRef.current.getContext("2d");
      txBar.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: ["24h", "7d", "30d"],
          datasets: [
            {
              label: "Transactions",
              data: [d.tx24h, d.tx7d, d.tx30d],
              backgroundColor: ["#ec4899", "#3b82f6", "#9333ea"],
              borderRadius: 10,
            },
          ],
        },
        options: { plugins: { legend: { display: false } }, responsive: true },
      });
    }

    if (userLineRef.current) {
      const ctx = userLineRef.current.getContext("2d");
      userLine.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: d.userGrowth.labels,
          datasets: [
            {
              label: "User Growth",
              data: d.userGrowth.values,
              borderColor: "#3b82f6",
              backgroundColor: "rgba(59,130,246,0.2)",
              fill: true,
              tension: 0.4,
            },
          ],
        },
        options: { plugins: { legend: { display: false } }, responsive: true },
      });
    }

    if (presaleBarRef.current) {
      const ctx = presaleBarRef.current.getContext("2d");
      presaleBar.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: ["Sold", "Remaining"],
          datasets: [
            {
              data: [d.presaleSold, d.presaleRemaining],
              backgroundColor: ["#3b82f6", "#ec4899"],
            },
          ],
        },
        options: { plugins: { legend: { display: false } }, responsive: true },
      });
    }

    if (holdersDonutRef.current) {
      const ctx = holdersDonutRef.current.getContext("2d");
      holdersDonut.current = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Top Holders", "Others"],
          datasets: [
            {
              data: [d.topHoldersPercentage, 100 - d.topHoldersPercentage],
              backgroundColor: ["#ec4899", "#3b82f6"],
            },
          ],
        },
        options: { cutout: "70%", plugins: { legend: { display: false } } },
      });
    }
  }

  // === Actions Admin ===
  async function handleAirdrop() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/airdrop`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Airdrop failed");
      alert("✅ Airdrop sent successfully!");
      fetchStats();
    } catch (e) {
      alert(e.message);
    }
  }

  async function handlePause() {
    if (!window.confirm("Pause or unpause the contract?")) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/pause`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Pause failed");
      const data = await res.json();
      alert(`Contract is now ${data.paused ? "Paused" : "Active"}`);
      fetchStats();
    } catch (e) {
      alert(e.message);
    }
  }

  function handleLogout() {
    localStorage.clear();
    window.location.href = "/";
  }

  // === États ===
  if (checkingAuth)
    return (
      <div className="flex items-center justify-center h-screen text-white">
        <p className="text-xl animate-pulse">Checking admin access...</p>
      </div>
    );

  if (!isAdmin)
    return (
      <div className="flex items-center justify-center h-screen text-white">
        <p className="text-xl text-red-500">⛔ Access denied (Not admin wallet)</p>
      </div>
    );

  if (!stats)
    return (
      <div className="flex items-center justify-center h-screen text-white">
        <p className="text-xl animate-pulse">Loading {TOKEN_SYMBOL} admin data...</p>
      </div>
    );

  // === Interface principale ===
  return (
    <div className="min-h-screen bg-darkBg text-white">
      <header className="sticky top-0 bg-darkBg/90 backdrop-blur border-b border-gray-800 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => setSidebarOpen((s) => !s)}
            className="md:hidden p-2 rounded-lg bg-gray-800 hover:bg-gray-700"
          >
            <i data-feather="menu" className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-neonPink to-neonBlue bg-clip-text text-transparent">
            NEXORA Admin
          </h1>
          <button
            onClick={handleLogout}
            className="px-3 py-2 bg-red-600 rounded-lg hover:bg-red-700"
          >
            Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
        <aside
          className={`fixed md:static inset-y-0 left-0 w-64 transform ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 z-50 bg-darkBg border-r border-gray-800`}
        >
          <nav className="h-full p-4 space-y-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => {
                  setActive(t.key);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-gray-800 ${
                  active === t.key ? "bg-gray-800 border border-neonPink/30" : ""
                }`}
              >
                <i data-feather={t.icon} className="w-4 h-4" />
                <span>{t.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="min-h-[70vh]">
          {active === "overview" && <Overview stats={stats} />}
          {active === "stats" && (
            <StatsGlobales
              presaleBarRef={presaleBarRef}
              holdersDonutRef={holdersDonutRef}
            />
          )}
          {active === "txs" && (
            <Transactions txBarRef={txBarRef} userLineRef={userLineRef} stats={stats} />
          )}
          {active === "system" && (
            <SystemActions
              stats={stats}
              wallet={wallet}
              handleAirdrop={handleAirdrop}
              handlePause={handlePause}
            />
          )}
        </main>
      </div>
    </div>
  );
}

/* ==================== Sous-composants ==================== */
function Overview({ stats }) {
  return (
    <div>
      <h2 className="text-xl font-bold mb-6">Overview</h2>
      <div className="grid md:grid-cols-4 gap-6 mb-10">
        <Stat title="Total Users" value={stats.totalUsers} color="neonPink" />
        <Stat title="Token Holders" value={stats.tokenHolders} color="neonBlue" />
        <Stat title="Transactions 24h" value={stats.tx24h} color="neonPurple" />
        <Stat title="Treasury (ETH)" value={stats.treasuryBalance} color="neonPink" />
      </div>
    </div>
  );
}

function Stat({ title, value, color }) {
  return (
    <div className={`bg-gray-900 p-6 rounded-xl border border-${color}/40 shadow-lg`}>
      <h3 className={`text-${color} text-lg font-semibold mb-2`}>{title}</h3>
      <div className="text-3xl font-bold">{value}</div>
    </div>
  );
}

function StatsGlobales({ presaleBarRef, holdersDonutRef }) {
  return (
    <div className="grid md:grid-cols-2 gap-8 mb-12">
      <div className="bg-gray-900 p-6 rounded-xl border border-neonBlue/40">
        <h3 className="text-neonBlue mb-4 font-bold">Presale Progress</h3>
        <canvas ref={presaleBarRef}></canvas>
      </div>
      <div className="bg-gray-900 p-6 rounded-xl border border-neonPink/40">
        <h3 className="text-neonPink mb-4 font-bold">Holders Distribution</h3>
        <canvas ref={holdersDonutRef}></canvas>
      </div>
    </div>
  );
}

function Transactions({ txBarRef, userLineRef }) {
  return (
    <div className="grid md:grid-cols-2 gap-8 mb-12">
      <div className="bg-gray-900 p-6 rounded-xl border border-neonBlue/40">
        <h3 className="text-neonBlue mb-4 font-bold">Transactions</h3>
        <canvas ref={txBarRef}></canvas>
      </div>
      <div className="bg-gray-900 p-6 rounded-xl border border-neonPink/40">
        <h3 className="text-neonPink mb-4 font-bold">User Growth</h3>
        <canvas ref={userLineRef}></canvas>
      </div>
    </div>
  );
}

function SystemActions({ stats, wallet, handleAirdrop, handlePause }) {
  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="bg-gray-900 p-6 rounded-xl border border-neonBlue/40">
        <h3 className="text-neonBlue mb-6 font-bold">Admin Actions</h3>
        <button
          onClick={handleAirdrop}
          className="w-full py-3 bg-gradient-to-r from-neonPurple to-neonPink rounded-lg font-semibold hover:opacity-90"
        >
          Send Airdrop
        </button>
        <button
          onClick={handlePause}
          className="w-full mt-4 py-3 bg-red-600 rounded-lg hover:bg-red-700 font-semibold"
        >
          Pause Contract
        </button>
      </div>
      <div className="bg-gray-900 p-6 rounded-xl border border-neonPink/40">
        <h3 className="text-neonPink mb-6 font-bold">System Status</h3>
        <p>Status: {stats.paused ? "⛔ Paused" : "✅ Active"}</p>
        <p>Treasury: {stats.treasuryBalance} ETH</p>
        <p>Admin Wallet: {wallet.slice(0, 6)}...{wallet.slice(-4)}</p>
      </div>
    </div>
  );
}
